// Copyright (c) Meta Platforms, Inc. and affiliates.
// All rights reserved.

import { SRC_URL } from '@/server/apiConfig'
import { SEGTYPE_CLICK } from './config'
// This source code is licensed under the license found in the
// LICENSE file in the root directory of this source tree.

// Convert the onnx model mask prediction to ImageData
function arrayToImageData(input: any, width: number, height: number, segmentType: string | null) {
  // console.log('to image data')
  let [r, g, b, a] = [0, 114, 189, 255] // the masks's blue color
  const arr = new Uint8ClampedArray(4 * width * height).fill(0)
  for (let i = 0; i < input.length / 4; i++) {
    // Threshold the onnx model mask prediction at 0.0
    // This is equivalent to thresholding the mask using predictor.model.mask_threshold
    // in python
    if (input[i] > 0.0) {
      if (segmentType == SEGTYPE_CLICK) {
        r = g = b = a = 255
      }
      arr[4 * i + 0] = r
      arr[4 * i + 1] = g
      arr[4 * i + 2] = b
      arr[4 * i + 3] = a
    } else {
      if (segmentType == SEGTYPE_CLICK) {
        arr[4 * i + 3] = 255
      }
    }
  }
  return new ImageData(arr, width, height)
}
function addMaskToImageData(input: any, imgData: ImageData, overCircle: number | null) {
  // console.log('mask to image data')
  const color = input['id'] == overCircle ? [0, 114, 189, 255] : input['color']
  const [r, g, b, a] = color
  const [x, y, w, h] = input['bbox']
  const arr = imgData.data
  const width = imgData.width
  const height = imgData.height
  let p = 0

  for (let i = y; i < y + h; i++) {
    for (let j = x; j < x + w; j++) {
      const pos = i * width + j //在img中索引
      if (input['segmentation'][p] == 1) {
        arr[4 * pos + 0] = r
        arr[4 * pos + 1] = g
        arr[4 * pos + 2] = b
        arr[4 * pos + 3] = a
      }
      p++
    }
  }
  return new ImageData(arr, width, height)
}

function getClipImageDataAndBbox(
  input: any,
  width: number,
  height: number,
  imgArr: Uint8ClampedArray,
  resultMaskData: Uint8ClampedArray
) {
  // console.log('getClipImageData')
  let [minX, minY, maxX, maxY] = [width, height, -1, -1]
  let area = 0
  // console.log(`width: ${width}, height: ${height}`)
  // console.log(input.length)
  for (let i = 0; i < input.length / 4; i++) {
    if (input[i] > 0.0) {
      //计算包围盒
      const x = i % width
      const y = Math.floor(i / width)
      // console.log(x)
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x)
      maxY = Math.max(maxY, y)
    }
  }
  // console.log(`minX: ${minX}, minY: ${minY},maxX: ${maxX}, maxY: ${maxY}`)
  const [boxWidth, boxHeight] = [maxX - minX + 1, maxY - minY + 1]
  // console.log(`boxWidth: ${boxWidth}, boxHeight: ${boxHeight}`)
  const arr = new Uint8ClampedArray(4 * boxWidth * boxHeight).fill(0)
  let p = 0 //裁剪后索引
  for (let i = minY; i <= maxY; i++) {
    for (let j = minX; j <= maxX; j++) {
      const k = i * width + j //原图位置
      if (input[k] > 0.0 && resultMaskData[4 * k + 3] <= 0) {
        arr[4 * p + 0] = imgArr[4 * k + 0]
        arr[4 * p + 1] = imgArr[4 * k + 1]
        arr[4 * p + 2] = imgArr[4 * k + 2]
        arr[4 * p + 3] = imgArr[4 * k + 3]
        area++
      }
      p++
    }
  }
  const result = {
    imgData: new ImageData(arr, boxWidth, boxHeight),
    bbox: [minX, minY, boxWidth, boxHeight],
    area: area
  }
  return result
}

// Use a Canvas element to produce an image from ImageData
export function imageDataToImage(imageData: ImageData) {
  const canvas = imageDataToCanvas(imageData)
  const image = new Image()
  image.src = canvas.toDataURL()
  image.width = imageData.width
  image.height = imageData.height
  // console.log(image)
  return image
}

// Canvas elements can be created from ImageData
function imageDataToCanvas(imageData: ImageData) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  canvas.width = imageData.width
  canvas.height = imageData.height
  ctx?.putImageData(imageData, 0, 0)
  return canvas
}

export function imageToImageData(img: HTMLImageElement) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  canvas.width = img.width
  canvas.height = img.height
  ctx?.drawImage(img, 0, 0)
  return ctx?.getImageData(0, 0, img.width, img.height) as ImageData
}
export function imageToBase64(img: HTMLImageElement) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  canvas.width = img.width
  canvas.height = img.height
  ctx?.drawImage(img, 0, 0)
  const base64 = canvas.toDataURL('image/png') // 可根据需要更改图像类型
  return base64
}
export function base64ToImageElement(base64String: string): Promise<HTMLImageElement> {
  return new Promise((resolve) => {
    const image = new Image()
    image.setAttribute('crossOrigin', 'Anonymous')
    image.onload = () => {
      image.width = image.naturalWidth
      image.height = image.naturalHeight
      resolve(image)
      // console.log("solve", image)
    }
    image.src = base64String
    // console.log('load')
  })
}

// Convert the onnx model mask output to an HTMLImageElement
export function onnxMaskToImage(
  input: any,
  width: number,
  height: number,
  segmentType: string | null
) {
  return imageDataToImage(arrayToImageData(input, width, height, segmentType))
}

export function clipImageDataToStick(
  input: any,
  width: number,
  height: number,
  imgArr: Uint8ClampedArray,
  resultMask: any
) {
  const resp = getClipImageDataAndBbox(
    input,
    width,
    height,
    imgArr,
    imageToImageData(resultMask).data
  )
  const result = {
    img: imageDataToImage(resp.imgData),
    bbox: resp.bbox,
    area: resp.area,
    color: [
      Math.floor(Math.random() * 255) + 1,
      Math.floor(Math.random() * 255) + 1,
      Math.floor(Math.random() * 255) + 1,
      255
    ],
    name: null
  }
  return result
}
//通过everything中的ImageData叠加彩色蒙板
export function everythingImageDataToImage(
  input: any,
  width: number,
  height: number,
  overCircle: number | null
) {
  // console.log(width, height)
  // console.log('addMask')
  const arr = new Uint8ClampedArray(4 * width * height).fill(0)
  let imgData = new ImageData(arr, width, height)
  input.forEach((item: any) => {
    // console.log(item['bbox'])
    if (item['display']) {
      imgData = addMaskToImageData(item, imgData, overCircle)
    }
  })
  return imageDataToImage(imgData)
}
//通过everything中的ImageData获取剪切结果列表
export function everythingImageDataToSticker(input: any, imgData: ImageData, resultMask: any) {
  let results: any[] = []
  // console.log('everything to stickers')
  const resultMaskData = imageToImageData(resultMask).data
  for (let i = input.length - 1; i >= 0; i--) {
    const item = input[i]
    // input.forEach((item: any) => {
    if (item['display']) {
      let flag = 0
      const [x, y, boxWidth, boxHeight] = item['bbox']
      const arr = new Uint8ClampedArray(4 * boxWidth * boxHeight).fill(0)
      let p = 0 //裁剪后索引
      for (let i = y; i < y + boxHeight; i++) {
        for (let j = x; j < x + boxWidth; j++) {
          const k = i * imgData.width + j //原图位置
          if (item['segmentation'][p] == 1 && resultMaskData[4 * k + 3] <= 0) {
            arr[4 * p + 0] = imgData.data[4 * k + 0]
            arr[4 * p + 1] = imgData.data[4 * k + 1]
            arr[4 * p + 2] = imgData.data[4 * k + 2]
            arr[4 * p + 3] = imgData.data[4 * k + 3]
            resultMaskData[4 * k + 3] = 1
            flag = 1
          }
          p++
        }
      }
      if (flag) {
        const result = new ImageData(arr, boxWidth, boxHeight)
        const tmp = {
          img: imageDataToImage(result),
          bbox: item['bbox'],
          color: item['color'],
          pointer_coords: item['point_coords'],
          crop_box: [imgData.width, imgData.height],
          name: null
        }
        // results.push(tmp)
        results = [tmp, ...results]
      }
    }
  }
  return results
}
export function sitckersToResultMask(input: any, width: number, height: number) {
  const arr = new Uint8ClampedArray(4 * width * height).fill(0)
  // console.log(input)
  input.forEach((item: any) => {
    // console.log(item)
    const [x, y, boxWidth, boxHeight] = item['bbox']
    const imgData = imageToImageData(item['img'])
    // console.log(imgData)
    let p = 0
    for (let i = y; i < y + boxHeight; i++) {
      for (let j = x; j < x + boxWidth; j++) {
        const k = i * width + j //原图位置
        if (imgData.data[4 * p + 3] && arr[4 * k + 3] <= 0) {
          //不覆盖
          //该色块非空
          arr[4 * k + 0] = item['color'][0]
          arr[4 * k + 1] = item['color'][1]
          arr[4 * k + 2] = item['color'][2]
          arr[4 * k + 3] = item['color'][3]
        }
        p++
      }
    }
  })
  return imageDataToImage(new ImageData(arr, width, height))
}
export function stickToHighLightMask(input: any, width: number, height: number) {
  const [x, y, w, h] = input['bbox']
  const arr = new Uint8ClampedArray(4 * width * height).fill(0)
  const imgData = imageToImageData(input['img'])
  let k = 0
  for (let p = 0; p < arr.length / 4; p++) {
    const px = p % width
    const py = Math.floor(p / width)
    if (x <= px && px < x + w && y <= py && py < y + h) {
      if (imgData.data[4 * k + 3] == 0) {
        arr[4 * p + 3] = 255
      } else {
        arr[4 * p + 3] = arr[4 * p + 2] = arr[4 * p + 1] = arr[4 * p] = 255
      }
      k++
    } else arr[4 * p + 3] = 255
  }
  return imageDataToImage(new ImageData(arr, width, height))
}

export const downloadImage = (src: any) => {
  const imageUrl = `${SRC_URL}${src}`
  fetch(imageUrl)
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const lastSlashIndex = src.lastIndexOf('/')
      const substring = src.substring(lastSlashIndex + 1).replace(/\.jpg$/, '.png')
      a.download = substring
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
    })
}
